import { FC, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import StatusBadge from './StatusBadge';
import type { RequirementThread } from './types';

interface MergePanelProps {
  /** The selected WIP source threads to merge. */
  sources: RequirementThread[];
  /** After a Merge is proposed: the new PENDING_MERGE target + the locked (WIP) sources. */
  pendingTarget: RequirementThread | null;
  pendingSources: RequirementThread[];
  /**
   * The integration target's current text. Shown (and editable) once a Merge is
   * proposed so the reviewer can revise the combined requirement before Accept
   *. Empty before proposing.
   */
  targetText?: string;
  /**
   * Map of threadId → current requirement text for each source thread.
   * Used in review mode to show what each source contains.
   */
  sourcesTexts?: Record<string, string>;
  busy: boolean;
  /**
   * Review re-entry: opened for an already-pending Merge (target
   * is PENDING_MERGE with locked WIP sources), not a fresh proposal.
   */
  reviewMode?: boolean;
  /**
   * The role may propose, edit, accept and reject a Merge (collaborator and
   * above). Without it the review is read-only.
   */
  canWrite: boolean;
  onBack: () => void;
  onPropose: (name: string, text: string) => Promise<boolean>;
  onAccept: () => Promise<boolean>;
  onReject: () => Promise<boolean>;
  /** Save edited target text (creates a new CURRENT version). */
  onEditTarget?: (text: string) => Promise<boolean>;
}

const useStyles = makeStyles(theme => ({
  banner: { marginBottom: theme.spacing(2) },
  columns: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
  },
  sourceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  targetText: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 3,
    padding: theme.spacing(1.5),
    whiteSpace: 'pre-wrap',
    background: theme.palette.action.hover,
    marginBottom: theme.spacing(1),
  },
}));

export const MergePanel: FC<MergePanelProps> = ({
  sources,
  pendingTarget,
  pendingSources,
  targetText = '',
  sourcesTexts = {},
  busy,
  reviewMode = false,
  canWrite,
  onBack,
  onPropose,
  onAccept,
  onReject,
  onEditTarget,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-lifecycle');

  const [name, setName] = useState('');
  const [text, setText] = useState('');

  // Inline edit of the proposed target text before Accept.
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editError, setEditError] = useState(false);

  const proposed = reviewMode || pendingTarget !== null;
  const shownSources = proposed ? pendingSources : sources;

  const canPropose = name.trim() !== '' && text.trim() !== '';

  let bannerText: string;
  if (reviewMode) {
    bannerText = t('merge.bannerReview', { count: shownSources.length });
  } else if (proposed) {
    bannerText = t('merge.bannerPending', { count: shownSources.length });
  } else {
    bannerText = t('merge.banner', { count: shownSources.length });
  }

  const startEdit = () => {
    setEditText(targetText);
    setEditError(false);
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!onEditTarget) return;
    setEditError(false);
    const ok = await onEditTarget(editText.trim());
    if (ok) {
      setEditing(false);
    } else {
      setEditError(true);
    }
  };

  const editChanged =
    editText.trim() !== targetText.trim() && editText.trim() !== '';

  return (
    <Box>
      <Button onClick={onBack} size="small">
        {t('common.buttons.back')}
      </Button>

      <Alert severity="info" className={classes.banner}>
        {bannerText}
      </Alert>

      <Box className={classes.columns}>
        {/* Left: sources (locked once pending). */}
        <Card>
          <CardContent>
            <Typography variant="subtitle2">
              {t('merge.sourcesTitle')}
            </Typography>
            {shownSources.map(s => (
              <Box key={s.threadId} className={classes.sourceRow}>
                <Box>
                  <span>
                    {proposed ? <span aria-hidden="true">🔒 </span> : ''}
                    {s.name || `#${s.threadId}`}
                  </span>
                  <StatusBadge status={s.status} />
                  {sourcesTexts[s.threadId] && (
                    <Typography variant="body2" color="textSecondary">
                      {sourcesTexts[s.threadId]}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Right: merged target (name editable until proposed; text revisable
            while pending via onEditTarget). */}
        <Card>
          <CardContent>
            <Typography variant="subtitle2">
              {t('merge.targetTitle')}
            </Typography>
            {proposed && pendingTarget ? (
              <Box>
                <Box className={classes.sourceRow}>
                  <strong>
                    {pendingTarget.name || `#${pendingTarget.threadId}`}
                  </strong>
                  <StatusBadge status={pendingTarget.status} />
                </Box>
                {editing ? (
                  <Box style={{ marginTop: 8 }}>
                    <TextField
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      fullWidth
                      multiline
                      minRows={4}
                      variant="outlined"
                      disabled={busy}
                      inputProps={{ 'data-testid': 'merge-target-edit-text' }}
                    />
                    {editError && (
                      <Alert severity="error">
                        {t('merge.editTargetError')}
                      </Alert>
                    )}
                    <Box className={classes.actions}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={busy || !editChanged}
                        onClick={saveEdit}
                      >
                        {t('common.buttons.save')}
                      </Button>
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => setEditing(false)}
                      >
                        {t('common.buttons.cancel')}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box style={{ marginTop: 8 }}>
                    <div className={classes.targetText}>
                      {targetText || '—'}
                    </div>
                    {canWrite && onEditTarget && (
                      <Button size="small" disabled={busy} onClick={startEdit}>
                        {t('merge.editTarget')}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <>
                <TextField
                  label={t('merge.targetName')}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  fullWidth
                  disabled={busy}
                  style={{ marginBottom: 8 }}
                  inputProps={{ 'data-testid': 'merge-target-name' }}
                />
                <TextField
                  label={t('merge.targetText')}
                  value={text}
                  onChange={e => setText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                  variant="outlined"
                  disabled={busy}
                  inputProps={{ 'data-testid': 'merge-target-text' }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {canWrite && (
        <Box className={classes.actions}>
          {proposed ? (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={onAccept}
                disabled={busy || pendingTarget === null}
                endIcon={busy ? <CircularProgress size={18} /> : undefined}
              >
                {t('merge.accept')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onReject}
                disabled={busy || pendingTarget === null}
              >
                {t('merge.reject')}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => onPropose(name.trim(), text.trim())}
              disabled={busy || !canPropose}
              endIcon={busy ? <CircularProgress size={18} /> : undefined}
            >
              {t('merge.propose', { count: shownSources.length })}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MergePanel;
